import React, { useEffect, useMemo, useState } from 'react';
import {
  alternateLabelsForSelectors,
  formatOrderDateToLocalTime,
  getMappedValueFromOrderOrItem,
  priceFormat,
  splitAtUnderscoreCamelCaseOrDotAndUpperCase,
} from '../../../../util';
import { CUSTOM_INFO_WIDGET_ORDER_FIELDS } from '../../../../constants/decisionTree';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import { getDecisionNodeAtPath, getSimplrAccountIdOfSelectedTicket } from '../../../../extractFromState';
import * as toolSelectors from '../../../../selectors/tools';
import { useSelector } from 'react-redux';
import { TrackingLinkOrInfo } from '../Common/TrackingLinkOrInfo/TrackingLinkOrInfo';
import { dateSelectors } from '../../../../util/decisionTreeUtil';
import { ResultsColumn, ResultsColumnHeader, Row, HeaderRow, Spreadsheet, RowVertical } from '../DTInfoWidget.styles';
import { CustomInfoSubTitleText } from './DTCustomInfoWidget.styles';
import { handleError } from '../../../../services/ErrorService/ErrorService';
import { fetchCustomInfoWidget } from '../../../../api/decisionTree';
import { Skeleton } from '../../../styled/skeleton';
import { ReturnTrackingLinkOrInfo } from '../Common/TrackingLinkOrInfo/ReturnTrackingLinkOrInfo';
import ordersSearchMapping from './OrdersSearchMapping/OrdersSearchMapping';
import ArrayInText from './ArrayInText/ArrayInText';

const DTCustomInfoWidget = () => {
  const { customInfoWidget = {} } = useSelector(getDecisionNodeAtPath);
  const selectedOrder = useSelector(toolSelectors.getSelectedOrder);
  const orderingSystem = useSelector(toolSelectors.getOrderingSystem);
  const selectedLineItems = useSelector(toolSelectors.getSelectedLineItems);
  const simplrAccountId = useSelector(getSimplrAccountIdOfSelectedTicket);
  const selectedOrderLineItems = useSelector(toolSelectors.getLineItemsOfSelectedOrder);
  const ordersSearch = useSelector(toolSelectors.getOrdersSearchProperties);

  let lineItems;
  if (!isEmpty(selectedLineItems)) {
    lineItems = selectedLineItems;
  } else {
    lineItems = selectedOrderLineItems;
  }

  const selectedSubscriptionList = useSelector(toolSelectors.getSelectedSubscriptionList);
  const selectedOrderShippingAmount = useSelector(toolSelectors.getSelectedOrderShippingAmount);
  const selectedReturn = useSelector(toolSelectors.getSelectedReturn);
  const returnItemsForSelectedReturn = useSelector(toolSelectors.getLineItemsOfSelectedReturn);
  const selectedAccount = useSelector(toolSelectors.getSelectedAccount);
  const selectedCustomer = useSelector(toolSelectors.getSelectedCustomer);

  const [infoWidget, setInfoWidget] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      if (!isString(customInfoWidget)) {
        return setInfoWidget(customInfoWidget);
      }

      try {
        setIsLoading(true);
        const widget = await fetchCustomInfoWidget(customInfoWidget);
        setInfoWidget(widget);
      } catch (error) {
        handleError({ error, showPopup: true, customFields: { customInfoWidget } });
      } finally {
        setIsLoading(false);
      }
    })();
  }, [simplrAccountId, customInfoWidget]);

  const renderSkeletonUI = () => {
    return (
      <RowVertical>
        <Skeleton height="18px" width="100%" />
        <Skeleton height="18px" width="100%" />
        <Skeleton height="18px" width="75%" />
      </RowVertical>
    );
  };

  // used for supporting backward compatibility for old data structure
  // old format - ["createdAt", "totalPriceOfItems"]
  // new format - {createdAt: "createdAt", totalPriceOfItems: "totalPriceOfItems", someCustomLabel: "dataPath"}
  const convertExistingDataToNewStructure = (data) => {
    if (Array.isArray(data)) {
      return data.reduce((acc, elem) => {
        acc[elem] = elem;

        return acc;
      }, {});
    }

    return data;
  };

  const itemData = useMemo(() => convertExistingDataToNewStructure(infoWidget.itemData) ?? {}, [infoWidget.itemData]);
  const orderData = useMemo(
    () => convertExistingDataToNewStructure(infoWidget.orderData) ?? {},
    [infoWidget.orderData]
  );
  const subscriptionData = useMemo(
    () => convertExistingDataToNewStructure(infoWidget.subscriptionData) ?? {},
    [infoWidget.subscriptionData]
  );
  const returnData = useMemo(
    () => convertExistingDataToNewStructure(infoWidget.returnData) ?? {},
    [infoWidget.returnData]
  );
  const returnItemData = useMemo(
    () => convertExistingDataToNewStructure(infoWidget.returnItemData) ?? {},
    [infoWidget.returnItemData]
  );
  const accountData = infoWidget.accountData ?? {};
  const ordersSearchData = useMemo(
    () => convertExistingDataToNewStructure(infoWidget.ordersSearchData) ?? {},
    [infoWidget.ordersSearchData]
  );
  const customerData = useMemo(
    () => convertExistingDataToNewStructure(infoWidget.customerData) ?? {},
    [infoWidget.customerData]
  );

  const getFormattedDateField = (key) => {
    const dateString = getMappedValueFromOrderOrItem(selectedOrder, key, orderingSystem);

    return dateString ? formatOrderDateToLocalTime(dateString, orderingSystem) : null;
  };

  if (isLoading) {
    return renderSkeletonUI();
  }

  return (
    <Spreadsheet reducePadding={itemData.length && orderData.length} className="fs-block">
      {!isEmpty(orderData) && (
        <>
          <CustomInfoSubTitleText>ORDER INFORMATION</CustomInfoSubTitleText>
          <HeaderRow>
            {Object.keys(orderData).map((key) => (
              <ResultsColumnHeader key={`order_data_label_${key}`}>
                {splitAtUnderscoreCamelCaseOrDotAndUpperCase(key)}
              </ResultsColumnHeader>
            ))}
          </HeaderRow>
          <Row extraBottomPadding={true}>
            {Object.values(orderData).map((key) => {
              if (key === CUSTOM_INFO_WIDGET_ORDER_FIELDS.TOTAL_PRICE_OF_ITEMS) {
                // pulls data from each line item, but should be displayed with order data
                return (
                  <ResultsColumn key={`order_data_${key}`}>
                    {String(getMappedValueFromOrderOrItem(selectedLineItems, key, orderingSystem))}
                  </ResultsColumn>
                );
              } else if (dateSelectors.includes(key)) {
                // date fields need to be converted to central time
                return <ResultsColumn key={`order_data_${key}`}>{getFormattedDateField(key)}</ResultsColumn>;
              } else if (key === CUSTOM_INFO_WIDGET_ORDER_FIELDS.SHIPPING_AMOUNT) {
                return (
                  <ResultsColumn key={`order_data_${key}`}>{priceFormat(selectedOrderShippingAmount)}</ResultsColumn>
                );
              }
              if (isString(key)) {
                // standard selector
                return (
                  <ResultsColumn key={`order_data_${key}`}>
                    {String(getMappedValueFromOrderOrItem(selectedOrder, key, orderingSystem))}
                  </ResultsColumn>
                );
              }

              // array or object selector
              return <ArrayInText arrayData={key} selectedItem={selectedOrder} orderingSystem={orderingSystem} />;
            })}
          </Row>
        </>
      )}
      {!isEmpty(itemData) && !isEmpty(lineItems) && (
        <>
          <HeaderRow>
            {Object.keys(itemData).map((key) => (
              <ResultsColumnHeader key={`item_data_label_${key}`}>
                {alternateLabelsForSelectors[key]
                  ? // label should be different from selector name
                    alternateLabelsForSelectors[key]
                  : // use selector as label
                    splitAtUnderscoreCamelCaseOrDotAndUpperCase(key)}
              </ResultsColumnHeader>
            ))}
          </HeaderRow>
          {lineItems.map((item, idx) => (
            <Row extraBottomPadding={lineItems.length < 2} key={idx}>
              {Object.values(itemData).map((key) =>
                isString(key) ? (
                  <ResultsColumn key={`item_data_${key}`}>
                    {key === 'trackingNumberAsLink' ? (
                      // use special local selector to render HTML link
                      <TrackingLinkOrInfo item={item} />
                    ) : (
                      String(getMappedValueFromOrderOrItem(item, key, orderingSystem))
                    )}
                  </ResultsColumn>
                ) : (
                  <ArrayInText arrayData={key} selectedItem={item} />
                )
              )}
            </Row>
          ))}
        </>
      )}
      {!isEmpty(subscriptionData) && (
        <>
          <CustomInfoSubTitleText>SUBSCRIPTION INFORMATION</CustomInfoSubTitleText>
          <HeaderRow>
            {Object.keys(subscriptionData).map((key) => (
              <ResultsColumnHeader key={`subscription_data_label=${key}`}>
                {splitAtUnderscoreCamelCaseOrDotAndUpperCase(key)}
              </ResultsColumnHeader>
            ))}
          </HeaderRow>
          {selectedSubscriptionList.map((subscription, idx) => (
            <Row extraBottomPadding={true} key={idx}>
              {Object.values(subscriptionData).map((key) =>
                isString(key) ? (
                  <ResultsColumn key={`subscription_data_${key}`}>
                    {String(getMappedValueFromOrderOrItem(subscription, key, orderingSystem))}
                  </ResultsColumn>
                ) : (
                  <ArrayInText arrayData={key} selectedItem={subscription} />
                )
              )}
            </Row>
          ))}
        </>
      )}
      {returnItemsForSelectedReturn && (!isEmpty(returnData) || !isEmpty(returnItemData)) && (
        <CustomInfoSubTitleText>RETURN INFORMATION</CustomInfoSubTitleText>
      )}
      {selectedReturn && !isEmpty(returnData) && (
        <>
          <HeaderRow>
            {Object.keys(returnData).map((key) => (
              <ResultsColumnHeader key={`return_data_label=${key}`}>
                {splitAtUnderscoreCamelCaseOrDotAndUpperCase(key)}
              </ResultsColumnHeader>
            ))}
          </HeaderRow>
          <Row extraBottomPadding={true}>
            {Object.values(returnData).map((key) => {
              if (['trackingNumberAsLink', 'statusPageUrl'].includes(key)) {
                return (
                  <ResultsColumn key={`return_data_${key}`}>
                    <ReturnTrackingLinkOrInfo item={selectedReturn} selectedKey={key} />
                  </ResultsColumn>
                );
              } else if (isString(key)) {
                return <ResultsColumn key={`return_data_${key}`}>{String(get(selectedReturn, key, ''))}</ResultsColumn>;
              }

              return <ArrayInText arrayData={key} selectedItem={selectedReturn} />;
            })}
          </Row>
        </>
      )}
      {returnItemsForSelectedReturn && !isEmpty(returnItemData) && (
        <>
          <HeaderRow>
            {Object.keys(returnItemData).map((key) => (
              <ResultsColumnHeader key={`return_item_data_label=${key}`}>
                {splitAtUnderscoreCamelCaseOrDotAndUpperCase(key)}
              </ResultsColumnHeader>
            ))}
          </HeaderRow>
          {returnItemsForSelectedReturn.map((item, idx) => (
            <Row extraBottomPadding={returnItemsForSelectedReturn.length < 2} key={idx}>
              {Object.values(returnItemData).map((key) => {
                if (key === 'trackingNumberAsLink') {
                  return <ReturnTrackingLinkOrInfo item={item} />;
                } else if (isString(key)) {
                  return <ResultsColumn key={`return_item_data_${key}`}>{String(get(item, key))}</ResultsColumn>;
                }

                return <ArrayInText arrayData={key} selectedItem={item} />;
              })}
            </Row>
          ))}
        </>
      )}
      {!isEmpty(accountData) && (
        <>
          <CustomInfoSubTitleText>ACCOUNT INFORMATION</CustomInfoSubTitleText>
          <HeaderRow>
            {Object.keys(accountData).map((key) => (
              <ResultsColumnHeader key={`account_data_label_${key}`}>
                {splitAtUnderscoreCamelCaseOrDotAndUpperCase(key)}
              </ResultsColumnHeader>
            ))}
          </HeaderRow>
          <Row extraBottomPadding={true}>
            {Object.values(accountData).map((key) =>
              isString(key) ? (
                <ResultsColumn key={`account_data_${key}`}>{String(get(selectedAccount, key, ''))}</ResultsColumn>
              ) : (
                <ArrayInText arrayData={key} selectedItem={selectedAccount} />
              )
            )}
          </Row>
        </>
      )}
      {!isEmpty(ordersSearchData) && (
        <>
          <CustomInfoSubTitleText>ORDERS SEARCH INFORMATION</CustomInfoSubTitleText>
          <HeaderRow>
            {Object.keys(ordersSearchData).map((key) => (
              <ResultsColumnHeader key={`order_search_data_label_${key}`}>
                {splitAtUnderscoreCamelCaseOrDotAndUpperCase(key)}
              </ResultsColumnHeader>
            ))}
          </HeaderRow>
          <Row extraBottomPadding={true}>
            {Object.values(ordersSearchData).map((key) =>
              isString(key) ? (
                <ResultsColumn key={`order_search_data_${key}`}>
                  {String(ordersSearchMapping.getValue(orderingSystem, ordersSearch, key))}
                </ResultsColumn>
              ) : (
                <ArrayInText arrayData={key} selectedItem={ordersSearch} />
              )
            )}
          </Row>
        </>
      )}

      {!isEmpty(customerData) && (
        <>
          <CustomInfoSubTitleText>CUSTOMER INFORMATION</CustomInfoSubTitleText>
          <HeaderRow>
            {Object.keys(customerData).map((key) => (
              <ResultsColumnHeader key={`customer_data_label_${key}`}>
                {splitAtUnderscoreCamelCaseOrDotAndUpperCase(key)}
              </ResultsColumnHeader>
            ))}
          </HeaderRow>
          <Row extraBottomPadding={true}>
            {Object.values(customerData).map((key) =>
              isString(key) ? (
                <ResultsColumn key={`customer_data_${key}`}>{String(get(selectedCustomer, key, ''))}</ResultsColumn>
              ) : (
                <ArrayInText arrayData={key} selectedItem={selectedCustomer} />
              )
            )}
          </Row>
        </>
      )}
    </Spreadsheet>
  );
};
export default DTCustomInfoWidget;
